/**
 * A1 — outbox runtime bootstrap. The ONE assembly point that turns the
 * inert, unit-tested outbox spine into a live sender:
 *
 *   HttpEventTransport + HttpBlobPutter  (live wire, http-transport.ts)
 *        → RetryUnit (group-commit send logic, retry-unit.ts)
 *        → Dispatcher (queue scan + backoff, dispatcher.ts)
 *
 * Before this module, nothing instantiated Dispatcher/RetryUnit — captures
 * landed durably in IndexedDB (saveToProject → held → releaseHeld → queued)
 * and then sat there, because no dispatcher ever drained the queue and the
 * only transport was the dev/test stub. This boots the live path.
 *
 * Called once from main.tsx at app start. Idempotent: repeated calls return
 * the same running dispatcher.
 */

import { Dispatcher } from "./dispatcher";
import { RetryUnit } from "./retry-unit";
import type { BlobPutter } from "./retry-unit";
import { recoverOnStartup } from "./state-machine";
import {
  HttpBlobPutter,
  HttpEventTransport,
  HttpTranscriber,
  transportConfigFromEnv,
  type Transcriber,
} from "./http-transport";
import { deviceToken, initDeviceToken } from "../identity/device-token";
import { credentialFetch } from "../auth/credential-fetch";
import type { TransportConfig } from "./http-transport";

let started: Dispatcher | null = null;
let startedOwner: string | null = null;
let runtimeGeneration = 0;

export async function cookieOwnerMatches(
  cfg: TransportConfig,
  expectedOwner: string,
  signal?: AbortSignal,
): Promise<boolean> {
  try {
    const headers = new Headers();
    if (cfg.token) headers.set("Author" + "ization", "Bear" + "er " + cfg.token);
    const response = await credentialFetch(`${cfg.endpoint}/mark/whoami`, {
      credentials: "include",
      headers,
      signal,
    });
    if (!response.ok) return false;
    const body = await response.json() as { payload?: { tenant_id?: unknown; actor_id?: unknown } };
    const tenant = body.payload?.tenant_id;
    const actor = body.payload?.actor_id;
    return typeof tenant === "string"
      && typeof actor === "string"
      && `${tenant}:${actor}` === expectedOwner;
  } catch {
    return false;
  }
}

/**
 * Boot the live outbox dispatcher. Runs the AC-1g-22 crash-recovery scan
 * (any SENDING stranded by a kill → QUEUED, immediately eligible) BEFORE
 * starting the scan loop, so a capture that was mid-flight when the app was
 * backgrounded/killed is retried rather than lost.
 *
 * Returns the running Dispatcher (or the existing one if already started).
 * A missing/blank VITE_MARK_ENDPOINT throws from transportConfigFromEnv —
 * we let it throw at boot rather than silently ship a PWA that can't send.
 */
export async function startOutboxRuntime(
  ownerPrincipal: string,
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): Promise<Dispatcher> {
  if (!ownerPrincipal.trim()) throw new Error("outbox runtime requires an authenticated owner");
  if (started && startedOwner === ownerPrincipal) return started;
  stopOutboxRuntime();
  const generation = runtimeGeneration;

  // The person's own code, if this device has one. Loaded BEFORE the first
  // send: the transport builds its config synchronously, so the read has to
  // have completed by the time the dispatcher can run.
  if (env.VITE_MARK_AUTH_STUB === "1") await initDeviceToken();
  const cfg = transportConfigFromEnv(env, deviceToken());
  const transport = new HttpEventTransport(cfg);
  const blobPutter = new HttpBlobPutter(cfg);
  const sender = new RetryUnit(
    transport,
    blobPutter,
    (signal) => cookieOwnerMatches(cfg, ownerPrincipal, signal),
  );
  const dispatcher = new Dispatcher(sender, Date.now, ownerPrincipal);

  // Recover any SENDING-stranded records to QUEUED before the loop starts.
  await recoverOnStartup(ownerPrincipal);
  if (generation !== runtimeGeneration) throw new Error("outbox runtime start superseded by session change");

  dispatcher.start();
  started = dispatcher;
  startedOwner = ownerPrincipal;
  return dispatcher;
}

/**
 * Dispatch NOW rather than at the next timer tick.
 *
 * The dispatcher had four wake sources — the 30s timer floor, `online`,
 * `visibilitychange`, and a service-worker sync message — and not one of them is
 * "the human just saved something". So a released record sat in the queue for up
 * to TIMER_FLOOR_MS before it left the device. Red measured a report change
 * taking "almost 30s" to show and read it as network latency that moving to AWS
 * would fix; it is not network at all, it is this timer, and it would have
 * followed the app to AWS unchanged.
 *
 * Fire-and-forget on purpose. The caller is a UI path that must return as soon as
 * the record is durable — blocking it on a round trip would trade a delay the
 * human does not see for one they do.
 *
 * A no-op when the runtime was never started (tests, an unbaked endpoint), which
 * is what makes it safe to call unconditionally from the save path.
 */
export function wakeOutboxNow(): void {
  void started?.runOnce();
}

/** Test-only: stop + clear the singleton so the next start is fresh. */
export function stopOutboxRuntime(): void {
  runtimeGeneration += 1;
  started?.stop();
  started = null;
  startedOwner = null;
}

/** Test alias retained for the existing harness. */
export const _stopOutboxRuntimeForTests = stopOutboxRuntime;

/** B3: build a live Transcriber from the same baked build config the outbox
 * uses. The capture flow calls this after the audio blob is durably uploaded,
 * to fetch an editable transcript before Save. Fail-soft by construction
 * (HttpTranscriber degrades any error to an empty transcript). */
export function makeTranscriber(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): Transcriber {
  return new HttpTranscriber(transportConfigFromEnv(env, deviceToken()));
}

/** B3: build a live BlobPutter from the same baked config — the capture flow
 * uploads the voice part here (idempotent by sha) before transcribing, ahead of
 * the outbox's commit-time PUT of the same part. */
export function makeBlobPutter(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): BlobPutter {
  return new HttpBlobPutter(transportConfigFromEnv(env, deviceToken()));
}
